import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";

// Concircle Brand Colors
const BRAND_COLORS = {
  primary: "#217090", // Teal blue (from footer)
  lightBlue: "#93DBF9", // Light blue
  orange: "#FF7832", // Orange
  pink: "#FE416A", // Pink/Red
  lime: "#C3D624", // Lime green
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
  neutral: {
    50: "#F9FAFB",
    100: "#F3F4F6",
    200: "#E5E7EB",
    500: "#6B7280",
    700: "#374151",
    900: "#111827",
  },
};

// Define styles (similar to CSS but for PDFs)
const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingHorizontal: 40,
    paddingBottom: 90, // Extra space for footer with margins
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
  },

  // Cover Page
  coverContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 60,
  },
  coverBranding: {
    fontSize: 28,
    fontWeight: "bold",
    color: BRAND_COLORS.primary,
    marginBottom: 40,
    textAlign: "center",
    letterSpacing: 1,
  },
  coverBrandingText: {
    fontSize: 28,
    fontWeight: "bold",
    color: BRAND_COLORS.primary,
    letterSpacing: 1,
  },
  coverAccent: {
    width: 100,
    height: 4,
    backgroundColor: BRAND_COLORS.lightBlue,
    marginBottom: 30,
  },
  customerLogo: {
    width: 80,
    height: 80,
    objectFit: "cover",
    borderRadius: 8,
    marginBottom: 20,
  },
  coverTitle: {
    fontSize: 32,
    fontWeight: "bold",
    color: BRAND_COLORS.neutral[900],
    marginBottom: 10,
    textAlign: "center",
  },
  coverSubtitle: {
    fontSize: 16,
    color: BRAND_COLORS.neutral[500],
    marginBottom: 30,
    textAlign: "center",
  },
  coverCustomer: {
    fontSize: 24,
    fontWeight: "bold",
    color: BRAND_COLORS.neutral[700],
    marginTop: 20,
  },
  coverDate: {
    fontSize: 12,
    color: BRAND_COLORS.neutral[500],
    marginTop: 10,
  },
  releaseBadge: {
    backgroundColor: BRAND_COLORS.lightBlue,
    color: BRAND_COLORS.primary,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 20,
    alignSelf: "center",
  },

  // Section Headers
  sectionHeader: {
    fontSize: 20,
    fontWeight: "bold",
    color: BRAND_COLORS.neutral[900],
    marginTop: 30,
    marginBottom: 15,
    paddingBottom: 10,
    borderBottomWidth: 3,
    borderBottomColor: BRAND_COLORS.lightBlue,
  },
  sectionHeaderRed: {
    borderBottomColor: BRAND_COLORS.pink,
  },
  sectionHeaderGreen: {
    borderBottomColor: BRAND_COLORS.lime,
  },
  sectionHeaderOrange: {
    borderBottomColor: BRAND_COLORS.orange,
  },

  // Summary Cards Row
  summaryRow: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 20,
  },
  summaryCard: {
    flex: 1,
    padding: 15,
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  summaryCardBlue: {
    backgroundColor: "#EFF6FF",
    borderColor: "#BFDBFE",
  },
  summaryCardGreen: {
    backgroundColor: "#F0FDF4",
    borderColor: "#BBF7D0",
  },
  summaryCardOrange: {
    backgroundColor: "#FFF7ED",
    borderColor: "#FED7AA",
  },
  summaryCardRed: {
    backgroundColor: "#FEF2F2",
    borderColor: "#FECACA",
  },
  summaryCardYellow: {
    backgroundColor: "#FEFCE8",
    borderColor: "#FEF3C7",
  },
  summaryLabel: {
    fontSize: 10,
    color: "#6B7280",
    marginBottom: 6,
  },
  summaryValue: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#111827",
  },

  // Table
  table: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  tableHeaderCell: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#374151",
    textTransform: "uppercase",
  },
  tableRow: {
    flexDirection: "row",
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  tableRowAlt: {
    backgroundColor: "#F9FAFB",
  },
  tableCell: {
    fontSize: 9,
    color: "#111827",
  },
  tableCellGray: {
    color: "#6B7280",
  },

  // Impact Cards
  impactCard: {
    padding: 12,
    marginBottom: 8,
    borderRadius: 6,
    borderLeftWidth: 4,
  },
  impactCardCritical: {
    backgroundColor: "#FEF2F2",
    borderLeftColor: "#EF4444",
  },
  impactCardMedium: {
    backgroundColor: "#FFF7ED",
    borderLeftColor: "#F97316",
  },
  impactCardLow: {
    backgroundColor: "#EFF6FF",
    borderLeftColor: "#3B82F6",
  },
  impactTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#111827",
    marginBottom: 4,
  },
  impactDescription: {
    fontSize: 9,
    color: "#4B5563",
    marginBottom: 4,
  },
  impactServices: {
    fontSize: 8,
    color: "#6B7280",
  },

  // Badge
  badge: {
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 4,
    fontSize: 8,
    fontWeight: "bold",
    alignSelf: "flex-start",
  },
  badgeSuccess: {
    backgroundColor: "#D1FAE5",
    color: "#065F46",
  },
  badgeWarning: {
    backgroundColor: "#FED7AA",
    color: "#92400E",
  },
  badgeError: {
    backgroundColor: "#FECACA",
    color: "#991B1B",
  },
  badgeInfo: {
    backgroundColor: "#DBEAFE",
    color: "#1E40AF",
  },
  badgeLow: {
    backgroundColor: "#FEF3C7",
    color: "#92400E",
  },

  // Branded Footer
  footer: {
    marginTop: 30,
    marginBottom: 10,
    marginHorizontal: 0,
    backgroundColor: BRAND_COLORS.primary,
    paddingVertical: 12,
    paddingHorizontal: 25,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: 4,
  },
  footerLeft: {
    flex: 1,
  },
  footerCompany: {
    fontSize: 9,
    color: "#FFFFFF",
    fontWeight: "bold",
    marginBottom: 2,
  },
  footerWebsite: {
    fontSize: 7,
    color: "#FFFFFF",
    opacity: 0.9,
  },
  footerCenter: {
    flex: 1,
    alignItems: "center",
  },
  footerPageInfo: {
    fontSize: 7,
    color: "#FFFFFF",
    opacity: 0.8,
  },
  footerRight: {
    flex: 1,
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 6,
  },
  footerLogo: {
    width: 16,
    height: 16,
  },
  footerProduct: {
    fontSize: 8,
    color: "#FFFFFF",
    fontWeight: "bold",
  },
});

/**
 * Reusable Footer Component
 * IMPORTANT: wrap={false} prevents footer from splitting across pages
 * DO NOT use 'fixed' - it makes footer appear on every page
 */
const PDFFooter = ({ pageInfo }) => (
  <View style={styles.footer} wrap={false}>
    <View style={styles.footerLeft}>
      <Text style={styles.footerCompany}>Concircle Österreich GmbH</Text>
      <Text style={styles.footerWebsite}>www.concircle.com</Text>
    </View>
    <View style={styles.footerCenter}>
      {pageInfo && <Text style={styles.footerPageInfo}>{pageInfo}</Text>}
    </View>
    <View style={styles.footerRight}>
      <Image src="/white_icon.png" style={styles.footerLogo} />
      <Text style={styles.footerProduct}>conRIO</Text>
    </View>
  </View>
);

/**
 * Main PDF Document Component
 */
export function ReportPDFDocument({
  reportType,
  customer,
  services = [],
  impacts = null,
  whatsNew = null,
  releaseVersion,
}) {
  return (
    <Document>
      {/* Cover Page */}
      <Page size="A4" style={styles.page}>
        <View style={styles.coverContainer}>
          {/* Company Branding with Icon */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginBottom: 40,
            }}
          >
            <Text style={styles.coverBrandingText}>concircle</Text>
            <Image
              src="/logo192.png"
              style={{ width: 32, height: 32, marginHorizontal: 12 }}
            />
            <Text style={styles.coverBrandingText}>conRIO</Text>
          </View>
          <View style={styles.coverAccent} />

          {/* Customer Logo (only for customer-specific reports) */}
          {customer?.imageUrl &&
            customer.imageUrl.startsWith("data:image/") &&
            reportType !== "service-catalog" &&
            reportType !== "whats-new" && (
              <Image src={customer.imageUrl} style={styles.customerLogo} />
            )}

          {/* Report Title */}
          <Text style={styles.coverTitle}>{getReportTitle(reportType)}</Text>

          {/* Release Badge */}
          {releaseVersion && (
            <Text style={styles.releaseBadge}>Release: {releaseVersion}</Text>
          )}

          {/* Customer Info (only for customer-specific reports) */}
          {reportType !== "service-catalog" && reportType !== "whats-new" ? (
            <>
              <Text style={styles.coverCustomer}>{customer.name}</Text>
              {customer.industry && (
                <Text style={styles.coverSubtitle}>{customer.industry}</Text>
              )}
            </>
          ) : (
            <Text style={styles.coverSubtitle}>
              {reportType === "service-catalog"
                ? "Complete reference of all available SAP services"
                : "Latest features and capabilities"}
            </Text>
          )}

          {/* Generation Date */}
          <Text style={styles.coverDate}>
            Generated on{" "}
            {new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </Text>
        </View>

        {/* Footer - No spacer needed, coverContainer already uses flex:1 */}
        <PDFFooter pageInfo="Cover Page" />
      </Page>

      {/* Service Catalog - Single Continuous Section */}
      {(reportType === "service-catalog" ||
        reportType === "service-usage" ||
        reportType === "combined") && (
        <Page size="A4" style={styles.page} wrap>
          <Text style={styles.sectionHeader}>
            {reportType === "service-catalog"
              ? "Service Catalog"
              : "Service Usage"}
          </Text>

          {/* Summary */}
          <View style={{ marginBottom: 15 }}>
            <Text
              style={{
                fontSize: 11,
                color: "#4B5563",
                fontWeight: "bold",
              }}
            >
              Total Services: {services.length}
            </Text>
          </View>

          {/* Services Table - ALL services in one continuous table */}
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, { width: "25%" }]}>
                Service ID / Name
              </Text>
              <Text style={[styles.tableHeaderCell, { width: "57%" }]}>
                Description
              </Text>
              <Text style={[styles.tableHeaderCell, { width: "18%" }]}>
                Category
              </Text>
            </View>
            {services.map((service, index) => {
              // Handle both CustomerService (has currentVersion) and ServiceVersion (direct properties)
              const isCustomerService = !!service.currentVersion;
              const serviceId = isCustomerService
                ? service.serviceId
                : service.serviceId;
              const name = isCustomerService
                ? service.currentVersion?.name
                : service.name;
              const description = isCustomerService
                ? service.currentVersion?.description
                : service.description;
              const category = isCustomerService
                ? service.currentVersion?.category
                : service.category;

              return (
                <View
                  key={index}
                  style={[
                    styles.tableRow,
                    index % 2 === 1 && styles.tableRowAlt,
                  ]}
                  wrap={false}
                >
                  <Text
                    style={[styles.tableCell, { width: "25%", fontSize: 8 }]}
                  >
                    {serviceId}
                    {name && (
                      <Text style={{ fontSize: 7, color: "#6B7280" }}>
                        {"\n"}
                        {name}
                      </Text>
                    )}
                  </Text>
                  <Text
                    style={[
                      styles.tableCellGray,
                      { width: "63%", fontSize: 7, paddingRight: 5 },
                    ]}
                  >
                    {description || "No description available"}
                  </Text>
                  <Text
                    style={[
                      styles.tableCellGray,
                      { width: "12%", fontSize: 6, paddingLeft: 5 },
                    ]}
                  >
                    {category || "N/A"}
                  </Text>
                </View>
              );
            })}
          </View>

          {/* Spacer to push footer to bottom */}
          <View style={{ flexGrow: 1 }} />

          {/* Single Footer at the End */}
          <PDFFooter pageInfo={`Total Services: ${services.length}`} />
        </Page>
      )}

      {/* Change Impact Page */}
      {(reportType === "change-impact" || reportType === "combined") &&
        impacts && (
          <Page size="A4" style={styles.page} wrap>
            <Text style={[styles.sectionHeader, styles.sectionHeaderRed]}>
              Change Impact Analysis
            </Text>

            {/* Release Badge */}
            {releaseVersion && (
              <Text style={styles.releaseBadge}>Release: {releaseVersion}</Text>
            )}

            {/* Summary */}
            <View style={styles.summaryRow}>
              <View style={[styles.summaryCard]}>
                <Text style={styles.summaryLabel}>Total Services</Text>
                <Text style={styles.summaryValue}>
                  {impacts.total_services}
                </Text>
              </View>
              <View style={[styles.summaryCard, styles.summaryCardRed]}>
                <Text style={styles.summaryLabel}>Critical</Text>
                <Text style={styles.summaryValue}>
                  {impacts.summary.critical}
                </Text>
              </View>
              <View style={[styles.summaryCard, styles.summaryCardOrange]}>
                <Text style={styles.summaryLabel}>Medium</Text>
                <Text style={styles.summaryValue}>
                  {impacts.summary.medium}
                </Text>
              </View>
              <View style={[styles.summaryCard, styles.summaryCardYellow]}>
                <Text style={styles.summaryLabel}>Low</Text>
                <Text style={styles.summaryValue}>{impacts.summary.low}</Text>
              </View>
            </View>

            {/* Impacts Table */}
            {impacts.impacts.length === 0 ? (
              <View
                style={[
                  styles.summaryCard,
                  styles.summaryCardGreen,
                  { padding: 20 },
                ]}
              >
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: "bold",
                    color: "#065F46",
                    textAlign: "center",
                  }}
                >
                  ✓ No change impacts found for your services
                </Text>
              </View>
            ) : (
              <View style={styles.table}>
                <View style={styles.tableHeader}>
                  <Text style={[styles.tableHeaderCell, { width: "25%" }]}>
                    Service ID / Name
                  </Text>
                  <Text style={[styles.tableHeaderCell, { width: "60%" }]}>
                    Description
                  </Text>
                  <Text style={[styles.tableHeaderCell, { width: "15%" }]}>
                    Severity
                  </Text>
                </View>
                {impacts.impacts.map((impact, index) => (
                  <View
                    key={index}
                    style={[
                      styles.tableRow,
                      index % 2 === 1 && styles.tableRowAlt,
                    ]}
                    wrap={false}
                  >
                    <Text
                      style={[styles.tableCell, { width: "25%", fontSize: 8 }]}
                    >
                      {impact.service_ids.join(", ")}
                      {impact.title && (
                        <Text style={{ fontSize: 7, color: "#6B7280" }}>
                          {"\n"}
                          {impact.title}
                        </Text>
                      )}
                    </Text>
                    <Text
                      style={[
                        styles.tableCellGray,
                        { width: "60%", fontSize: 7, paddingRight: 5 },
                      ]}
                    >
                      {impact.description}
                    </Text>
                    <Text
                      style={[
                        styles.badge,
                        { width: "15%", fontSize: 7, alignSelf: "center" },
                        impact.severity === "CRITICAL"
                          ? styles.badgeError
                          : impact.severity === "MEDIUM"
                          ? styles.badgeWarning
                          : styles.badgeLow,
                      ]}
                    >
                      {impact.severity}
                    </Text>
                  </View>
                ))}
              </View>
            )}

            {/* Spacer to push footer to bottom */}
            <View style={{ flexGrow: 1 }} />

            <PDFFooter pageInfo="Change Impact Analysis" />
          </Page>
        )}

      {/* What's New Page (NOT included in combined report) */}
      {reportType === "whats-new" && whatsNew && (
        <Page size="A4" style={styles.page} wrap>
          <Text style={[styles.sectionHeader, styles.sectionHeaderGreen]}>
            What's New Features
          </Text>

          {/* Release Badge */}
          {releaseVersion && (
            <Text style={styles.releaseBadge}>Release: {releaseVersion}</Text>
          )}

          {/* Summary */}
          <View style={{ marginBottom: 15 }}>
            <Text
              style={{ fontSize: 14, color: "#374151", fontWeight: "bold" }}
            >
              Total New Features: {whatsNew.total_items}
            </Text>
          </View>

          {/* Features Table */}
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, { width: "25%" }]}>
                Service ID / Name
              </Text>
              <Text style={[styles.tableHeaderCell, { width: "60%" }]}>
                Description
              </Text>
              <Text style={[styles.tableHeaderCell, { width: "15%" }]}>
                Type
              </Text>
            </View>
            {whatsNew.items.map((item, index) => {
              return (
                <View
                  key={index}
                  style={[
                    styles.tableRow,
                    index % 2 === 1 && styles.tableRowAlt,
                  ]}
                  wrap={false}
                >
                  <Text
                    style={[styles.tableCell, { width: "25%", fontSize: 8 }]}
                  >
                    {item.reference_number}
                    {item.title && (
                      <Text style={{ fontSize: 7, color: "#6B7280" }}>
                        {"\n"}
                        {item.title}
                      </Text>
                    )}
                  </Text>
                  <Text
                    style={[
                      styles.tableCellGray,
                      { width: "60%", fontSize: 7, paddingRight: 5 },
                    ]}
                  >
                    {item.description || "No description available"}
                  </Text>
                  <Text
                    style={[styles.tableCell, { width: "15%", fontSize: 7 }]}
                  >
                    {item.type || "N/A"}
                  </Text>
                </View>
              );
            })}
          </View>

          {/* Spacer to push footer to bottom */}
          <View style={{ flexGrow: 1 }} />

          <PDFFooter pageInfo="What's New Features" />
        </Page>
      )}
    </Document>
  );
}

function getReportTitle(type) {
  const titles = {
    "service-catalog": "SAP Digital Manufacturing\nService Catalog",
    "change-impact": "Change Impact Analysis",
    "whats-new": "What's New Features",
    "service-usage": "Service Usage Report",
    combined: "Comprehensive Analysis Report",
  };
  return titles[type] || "Report";
}
